import { FormInstance, FormProps, message } from 'antd/lib'
import Form from 'antd/es/form/index'
import { useTranslation } from 'react-i18next'
import Input from 'antd/es/input'
import { convertPxToNumber } from '../../../../utils/helpers.ts'
import { selectThemeStyle } from '../../../../features/setting/setting.slice.ts'
import { useAppSelector } from '../../../../redux/hooks.ts'
import Row from 'antd/es/grid/row'
import Col from 'antd/es/grid/col'
import Select, { DefaultOptionType } from 'antd/es/select/index'
import {
  getNetCategories,
  regionOptions,
} from '../../../../utils/selector-options.ts'
import { useCallback, useEffect, useState } from 'react'
import { IdeyaButton } from '../../../../common/antd/ideya-button.tsx'
import { useCreateGroupMutation } from '../../../../apis/firm-management/firm-management-api.api.ts'
import { GroupCreateRequest } from '../../../../apis/firm-management/firm-management-api.def.ts'
import { getErrorDescription } from '../../../../redux/utils.ts'
import notification from 'antd/es/notification/index'

export type GroupManagementFormFieldTypes = {
  name: string
  region: string
  netCategory: string
  firmId: string
}

interface GroupManagementFormProps {
  onCancel: () => void
  form: FormInstance<GroupManagementFormFieldTypes>
}

export const GroupManagementForm: React.FC<GroupManagementFormProps> = ({
  onCancel,
  form,
}) => {
  const { t } = useTranslation()
  const themeStyle = useAppSelector(selectThemeStyle)
  const [netCategoryOptions, setNetCategoryOptions] = useState<
    DefaultOptionType[]
  >([])
  const regionValue = Form.useWatch('region', form)
  const [messageApi, messageContextHolder] = message.useMessage()
  const [notificationApi, notificationContextHolder] =
    notification.useNotification()
  const [createGroup, { isLoading, isSuccess, isError, error, reset }] =
    useCreateGroupMutation()

  useEffect(() => {
    if (regionValue) {
      const options = getNetCategories(regionValue)
      setNetCategoryOptions(options)
      form.setFieldValue('netCategory', undefined)
    }
  }, [regionValue, form])

  const onFinish: FormProps<GroupManagementFormFieldTypes>['onFinish'] = (
    values
  ) => {
    const request: GroupCreateRequest = {
      name: values.name,
      region: values.region,
      netCategory: values.netCategory,
      firmId: form.getFieldValue('firmId'),
    }
    createGroup(request)
  }

  const success = useCallback(() => {
    messageApi.open({
      type: 'success',
      content: t('firm_management.form.success_message'),
    })
  }, [messageApi, t])

  const errorNotification = useCallback(
    (description: string) => {
      notificationApi.error({
        message: 'Error',
        description,
        placement: 'topRight',
      })
    },
    [notificationApi]
  )

  useEffect(() => {
    if (isSuccess) {
      success()
      form.resetFields()
      onCancel()
      reset()
    }
  }, [isSuccess, form, onCancel, reset, success])

  useEffect(() => {
    if (isError) {
      const description = getErrorDescription(error)
      errorNotification(description)
      reset()
    }
  }, [isError, error, reset, errorNotification])

  const handleCancel = () => {
    form.resetFields()
    onCancel()
  }

  return (
    <>
      {messageContextHolder}
      {notificationContextHolder}
      <Form form={form} layout={'vertical'} onFinish={onFinish}>
        <Form.Item
          label={t('firm_management.group_modal.form.group_name')}
          name={'name'}
          rules={[
            {
              required: true,
              message: t(
                'firm_management.group_modal.form.please_enter_group_name'
              ),
            },
          ]}
        >
          <Input
            placeholder={t('firm_management.group_modal.form.group_name')}
          />
        </Form.Item>
        <Row
          gutter={[
            convertPxToNumber(themeStyle.marginBase),
            convertPxToNumber(themeStyle.marginBase),
          ]}
        >
          <Col span={12}>
            <Form.Item
              label={t('firm_management.group_modal.form.region')}
              name={'region'}
              rules={[
                {
                  required: true,
                  message: t(
                    'firm_management.group_modal.form.please_enter_region'
                  ),
                },
              ]}
            >
              <Select
                placeholder={t('ticker_management.form.select_region')}
                options={[
                  { value: 'Global', label: 'Global' },
                  ...regionOptions,
                ]}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t('firm_management.group_modal.form.net_category')}
              name={'netCategory'}
              rules={[
                {
                  required: true,
                  message: t(
                    'firm_management.group_modal.form.please_select_net_category'
                  ),
                },
              ]}
            >
              <Select
                placeholder={t(
                  'firm_management.group_modal.form.select_net_category'
                )}
                options={netCategoryOptions}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: themeStyle.marginXS,
            marginTop: themeStyle.marginXXL,
          }}
        >
          <IdeyaButton ideyaType={'secondary'} onClick={handleCancel}>
            {t('common.cancel')}
          </IdeyaButton>
          <IdeyaButton loading={isLoading} htmlType={'submit'}>
            {t('common.ok')}
          </IdeyaButton>
        </Row>
      </Form>
    </>
  )
}
